/**
 * Phone-system link — `https://pos.matix.one/customer-call?phoneNo=<number>`.
 *
 * The tenant's PBX / call-centre machine opens this address on every incoming
 * call, with the caller's number, in the browser where the till is signed in
 * (the old POS's `/Order/customerCall?phoneNo=…`). The till then shows that
 * customer, or the new-customer form with the number already typed.
 *
 * Read once, at import, and removed from the address bar. The number is also
 * kept in sessionStorage until it is used: a stale session answers the first
 * API call with a 401, which sends the page to /login without its query, and
 * the call must still be on screen once the cashier has signed back in.
 */

const STORAGE_KEY = 'pos:cashier:customer-call';

/** The number as the PBX sent it, digits only (a leading + or 00 dropped). */
export function readCustomerCallPhone(href: string): { phone: string | null; cleaned: string | null } {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return { phone: null, cleaned: null };
  }
  const raw = url.searchParams.get('phoneNo') ?? url.searchParams.get('phone');
  const isCallPath = url.pathname.replace(/\/+$/, '').toLowerCase() === '/customer-call';
  if (raw == null && !isCallPath) return { phone: null, cleaned: null };
  url.searchParams.delete('phoneNo');
  url.searchParams.delete('phone');
  const path = isCallPath ? '/' : url.pathname;
  const cleaned = path + url.search + url.hash;
  const digits = (raw ?? '').replace(/\D/g, '').replace(/^00/, '');
  return { phone: digits.length > 0 ? digits : null, cleaned };
}

/**
 * Does a stored customer phone belong to the caller? Equal digits, or one is
 * the other with a country code in front (the PBX may send 96569997892 for a
 * customer saved as 69997892, or the reverse). The shorter side must still be
 * a whole local number, so a 4-digit extension never matches a stranger.
 */
export function phoneMatchesCaller(stored: string | null | undefined, caller: string): boolean {
  const a = (stored ?? '').replace(/\D/g, '').replace(/^00/, '');
  if (!a || !caller) return false;
  if (a === caller) return true;
  const [short, long] = a.length < caller.length ? [a, caller] : [caller, a];
  return short.length >= 7 && long.endsWith(short);
}

let pending: string | null = null;
if (typeof window !== 'undefined') {
  const { phone, cleaned } = readCustomerCallPhone(window.location.href);
  if (cleaned !== null) {
    try {
      window.history.replaceState(window.history.state, '', cleaned);
    } catch {
      /* history unavailable — the call is still handled */
    }
  }
  try {
    if (phone) window.sessionStorage.setItem(STORAGE_KEY, phone);
    pending = phone ?? window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    pending = phone;
  }
}

/** Hands the caller's number over exactly once. */
export function takeCustomerCallPhone(): string | null {
  const phone = pending;
  pending = null;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing stored */
  }
  return phone;
}
